import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowIcon,
  CustomHeader,
  CustomTextContainer,
  Dot,
} from '@pages/new-design-components/custom-components';
import CustomSearch from '@pages/components/CustomSearch';
import { Col, Modal } from 'reactstrap';
import axios from 'axios';
import { useHistory, useLocation } from 'react-router-dom';
import { ButtonContainer } from '@pages/reusable-components/styled-components';
import DebtManagementTable from '@pages/components/DebtManagement/DebtManagementTable';
import { GET_DEBT_CUSTOMERS_REQUEST } from '@redux/Customer/actions';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import usePrevious from '@hooks/useprevious';
import { API_CORE_URL } from '@constants/index';
import CustomDamageDropDown from '@pages/components/JobDetailsComponents/CustomDropDown';
import Pagination from 'react-js-pagination';
import notify from '@utils/notify';

export default function DebtRecovery() {
  const dispatch = useDispatch();
  const location = useLocation();
  const [showQuickView, setShowQuickView] = useState(false);
  const [pageTitle, setPageTitle] = useState('Customers');
  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [keys, setKeys] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [orderFilter, setOrderFilter] = useState({});
  const [paymentManagers, setPaymentManagers] = useState([]);
  const [debtRecoverStatuses, setDebtRecoverStatuses] = useState([]);
  const [filter, setFilter] = useState({});
  const history = useHistory();
  const { getDebtCustomersSuccess, getDebtCustomersError, debtCustomers } = useSelector(
    (state) => state.customer,
    shallowEqual
  );
  const getDebtCustomersSuccessPrev = usePrevious(getDebtCustomersSuccess);
  const getDebtCustomersErrorPrev = usePrevious(getDebtCustomersError);
  const KEYS_MAP = {
    '/admin/repair-quotes': [
      { key: 'full_name', label: 'Customer', sort: true },
      { key: 'mobile_number', label: 'Phone Number' },
      { key: 'payment_manager', label: 'Payment Manager' },
      { key: 'customer_debt_sum_amount_due', label: 'Total Owed', sort: true },
      { key: 'customer_status', label: 'Customer Status' },
      { key: 'view', label: 'View' },
    ],

    '/admin/debt-recovery': [
      { key: 'full_name', label: 'Customer', sort: true },
      { key: 'mobile_number', label: 'Phone Number' },
      { key: 'debt_recovery_status', label: 'Debt Recovery Status' },
      { key: 'debt_recovery_decision', label: 'Debt Recovery Decision' },
      { key: 'payment_manager', label: 'Payment Manager' },
      { key: 'customer_debt_sum_amount_due', label: 'Total Owed', sort: true },
      { key: 'view', label: 'View' },
    ],

    '/admin/debt-overdue': [
      { key: 'full_name', label: 'Customer', sort: true },
      { key: 'mobile_number', label: 'Phone Number' },
      { key: 'payment_manager', label: 'Payment Manager' },
      { key: 'no_of_overdue', label: 'No of Overdue Invoices', sort: true },
      { key: 'overdue_duration', label: 'Overdue Duration', sort: true },
      { key: 'customer_debt_sum_amount_due', label: 'Total Owed', sort: true },
      { key: 'new_status', label: 'Status' },
      { key: 'expected_payment_date', label: 'Expected payment date' },
      { key: 'view', label: 'View' },
    ],

    '/admin/debt-defaulters': [
      { key: 'full_name', label: 'Customer', sort: true },
      { key: 'mobile_number', label: 'Phone Number' },
      { key: 'payment_manager', label: 'Payment Manager' },
      { key: 'no_of_overdue', label: 'No of Overdue Invoices', sort: true },
      { key: 'overdue_duration', label: 'Overdue Duration', sort: true },
      { key: 'expected_payment_date', label: 'Expected Payment Date', sort: true },
      { key: 'customer_debt_sum_amount_due', label: 'Total Owed', sort: true },
      { key: 'view', label: 'View' },
    ],

    '/admin/debt-initial-payment': [
      { key: 'full_name', label: 'Customer', sort: true },
      { key: 'mobile_number', label: 'Phone Number' },
      { key: 'payment_manager', label: 'Payment Manager' },
      { key: 'no_of_overdue', label: 'No of Overdue Invoices', sort: true },
      { key: 'overdue_duration', label: 'Overdue Duration', sort: true },
      { key: 'expected_payment_date', label: 'Expected Payment Date', sort: true },
      { key: 'customer_debt_sum_amount_due', label: 'Total Owed', sort: true },
      { key: 'view', label: 'View' },
    ],

    '/admin/debt-customers': [
      { key: 'full_name', label: 'Customer', sort: true },
      { key: 'mobile_number', label: 'Phone Number' },
      { object: 'customer_debt', key: 'no_of_outstanding', label: 'No of Outstanding Invoices' },
      { key: 'payment_manager', label: 'Payment Manager' },
      { key: 'customer_debt_sum_amount_due', label: 'Total Owed' },
      { key: 'view', label: 'View' },
    ],
  };

  const fetchData = (localFilter = {}) => {
    dispatch({
      type: GET_DEBT_CUSTOMERS_REQUEST,
      payload: { ...localFilter },
    });
  };

  const invoiceType = new URLSearchParams(location.search).get('invoice_type') || null;
  const invoiceTypeLabel =
    location.state?.invoiceTypeLabel ||
    (invoiceType || '').split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

  const clearInvoiceType = () => {
    const q = new URLSearchParams(location.search);
    q.delete('invoice_type');
    q.delete('page');
    history.push({ pathname: location.pathname, search: q.toString() });
  };

  const getPageFromUrl = () => {
    const q = new URLSearchParams(location.search);
    const p = parseInt(q.get('page') || '', 10);
    return Number.isFinite(p) && p > 0 ? p : null;
  };

  // Page changes push a history entry so Back returns to the previous page; clearing the page replaces it.
  const persistPageToUrl = (pageNumber) => {
    const q = new URLSearchParams(location.search);
    if (pageNumber && Number.isFinite(Number(pageNumber))) {
      q.set('page', String(pageNumber));
    } else {
      q.delete('page');
    }
    if (q.toString() === new URLSearchParams(location.search).toString()) return;
    const navigate = pageNumber ? history.push : history.replace;
    navigate({ pathname: location.pathname, search: q.toString(), state: location.state });
  };
  const updateKeysAndFiltersByRoute = (pathname) => {
    const routeKeys = KEYS_MAP[pathname];

    if (routeKeys && JSON.stringify(keys) !== JSON.stringify(routeKeys)) {
      setKeys(routeKeys);
    }
    const managerId = localStorage.getItem('payment_manager_id');
    const debtRecoveryStatus = localStorage.getItem('debt_recovery_status');
    const pageFromUrl = getPageFromUrl();
    const tempFilter = {
      page: pageFromUrl || 1,
      ...(managerId && { payment_manager_id: parseInt(managerId) }),
      ...(debtRecoveryStatus && { debt_recovery_status: debtRecoveryStatus }),
      ...(invoiceType && { invoice_type: invoiceType }),
    };
    switch (pathname) {
      case '/admin/repair-quotes':
        setPageTitle('Repair Quotes');
        setFilter(tempFilter);
        fetchData(tempFilter);
        break;

      case '/admin/debt-recovery':
        setPageTitle('Debt Recovery');
        setFilter({ customer_status: 'sent_to_debt_recovery', ...tempFilter });
        fetchData({ customer_status: 'sent_to_debt_recovery', ...tempFilter });
        break;

      case '/admin/debt-overdue':
        setPageTitle('Overdue');
        setFilter({
          customer_status: 'overdue',
          order_by: 'overdue_duration',
          order: 'asc',
          ...tempFilter,
        });
        setOrderFilter({
          order_by: 'overdue_duration',
          order: 'asc',
          ...tempFilter,
        });
        fetchData({
          customer_status: 'overdue',
          order_by: 'overdue_duration',
          order: 'asc',
          ...tempFilter,
        });
        break;

      case '/admin/debt-defaulters':
        setPageTitle('Defaulters');
        setFilter({ customer_status: 'sent_to_default_list', ...tempFilter });
        fetchData({ customer_status: 'sent_to_default_list', ...tempFilter });
        break;

      case '/admin/debt-initial-payment':
        setPageTitle('Initial Payment');
        setFilter({ customer_status: 'initial_payment', ...tempFilter });
        fetchData({ customer_status: 'initial_payment', ...tempFilter });
        break;

      case '/admin/debt-customers':
        setPageTitle('Customers');
        setFilter(tempFilter);
        fetchData(tempFilter);
        break;

      default:
        console.warn(`Unhandled pathname: ${pathname}`);
    }
  };
  // Single source of truth for fetching: the URL. A new route (or invoice type) rebuilds the filters,
  // a page change within the same route only refetches that page with the current filters.
  const lastRouteRef = useRef(null);
  useEffect(() => {
    if (!location?.pathname) return;
    const routeKey = `${location.pathname}|${invoiceType || ''}`;
    if (lastRouteRef.current !== routeKey) {
      lastRouteRef.current = routeKey;
      setLoading(true);
      updateKeysAndFiltersByRoute(location.pathname);
      return;
    }
    const pageFromUrl = getPageFromUrl() || 1;
    if (pageFromUrl !== (filter.page || 1)) {
      const tempFilter = { ...filter, page: pageFromUrl };
      setLoading(true);
      setFilter(tempFilter);
      fetchData(tempFilter);
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (getDebtCustomersSuccess && getDebtCustomersSuccessPrev === false) {
      axios({
        method: 'GET',
        url: `${API_CORE_URL}/account-managers`,
      })
        .then((res) => {
          let tempManagers = res.data.data.paymentManagers;
          setPaymentManagers(tempManagers);
          setTableData(debtCustomers?.data);
          setPagination(debtCustomers);
          setLoading(false);
        })
        .catch(() => {
          notify.warning('Warning', 'Something went wrong, please try again later');
        });

      axios({
        method: 'GET',
        url: `${API_CORE_URL}/debt-recovery-statuses`,
      }).then(res => {
        let tempStatuses = res.data.data;
        setDebtRecoverStatuses(tempStatuses);
      }).catch(() => {
        notify.warning('Warning', 'Something went wrong, please try again');
      });
    }
  }, [getDebtCustomersSuccess, getDebtCustomersSuccessPrev]);

  useEffect(() => {
    if (getDebtCustomersError && getDebtCustomersErrorPrev === false) {
      setLoading(false);
      notify.error('Something went wrong while getting stats!');
    }
  }, [getDebtCustomersError, getDebtCustomersErrorPrev]);

  const filterData = ({ key, value }) => {
    // Page changes go through the URL; the location effect does the fetch.
    if (key === 'page') {
      persistPageToUrl(value);
      return;
    }
    let tempFilter = { ...filter };
    tempFilter[key] = value;
    tempFilter['page'] = null;

    setLoading(true);
    setFilter(tempFilter);
    // if filters changed, remove page param
    persistPageToUrl(null);
    fetchData(tempFilter);
  };

  const viewDetails = (index) => {
    const data = tableData[index];
    const queryParams = new URLSearchParams({
      customer_id: data.id,
    }).toString();
    history.push({
      pathname: `/admin/customer-overview`,
      search: queryParams,
    });
    // const data = tableData[index];
    // const queryParams = new URLSearchParams({
    //     'customer_id': data.customer_id,
    //     'invoice_id': data.id,
    // }).toString();
    // history.push({
    //     pathname: `/admin/debt-recovery-overview`,
    //     search: queryParams
    // })
  };

  const toggleShowQuickViewModal = () => {
    setShowQuickView(!showQuickView);
  };
  const setOrderItem = (data) => {
    setOrderFilter(data);
    let tempFilter = { ...filter, ...data };
    setFilter(tempFilter);
    fetchData(tempFilter);
  };
  return (
    <div className={'container-fluid debt-background pt-3'}>
      <Modal
        isOpen={showQuickView}
        toggle={toggleShowQuickViewModal}
        modalClassName={'slide-in-from-right'}
        className={'modal-dialog-right'}
      >
        <div className={'p-3'}>
          <h6 className="modal-title text-center">Outstanding Payment</h6>
        </div>
        <div className={'modal-body'}>
          <div>
            <CustomHeader color={'#74787E'} fontSize={'14px'}>
              Customer:
            </CustomHeader>
          </div>
          <div>
            <CustomHeader color={'#74787E'} fontSize={'14px'}>
              Vehicle:
            </CustomHeader>
            <CustomHeader color={'#000000'} fontSize={'14px'} weight={'400'}>
              NA
            </CustomHeader>
          </div>
          <div>
            <CustomHeader color={'#74787E'} fontSize={'14px'}>
              Type:
            </CustomHeader>
            <CustomHeader color={'#000000'} fontSize={'14px'} weight={'400'}>
              NA
            </CustomHeader>
          </div>
          <div>
            <CustomHeader color={'#74787E'} fontSize={'14px'}>
              Status:
            </CustomHeader>
            <CustomHeader color={'#000000'} fontSize={'14px'} weight={'400'}>
              <Dot />
              text
            </CustomHeader>
          </div>
          <div>
            <CustomHeader color={'#74787E'} fontSize={'14px'}>
              Amount:
            </CustomHeader>
            <CustomHeader color={'#000000'} fontSize={'14px'} weight={'400'}>
              345`
            </CustomHeader>
          </div>
        </div>
        <div className="modal-footer justify-content-between">
          <ButtonContainer reverse onClick={toggleShowQuickViewModal}>
            {`Send a Reminder`}
          </ButtonContainer>
          <ButtonContainer onClick={toggleShowQuickViewModal}>{`Close`}</ButtonContainer>
        </div>
      </Modal>
      <CustomHeader lineHeight={'unset'}>{pageTitle}</CustomHeader>
      {invoiceType && (
        <div className={'d-flex mb-2'}>
          <span
            className={'d-inline-flex align-items-center'}
            style={{ background: '#E8F1FF', color: '#3F8CFF', borderRadius: '16px', padding: '4px 12px', fontSize: '14px' }}
          >
            Invoice type: {invoiceTypeLabel}
            <span
              className={'cursor-pointer ml-2'}
              role={'button'}
              title={'Clear invoice type filter'}
              onClick={clearInvoiceType}
            >
              ✕
            </span>
          </span>
        </div>
      )}

      <div className={'responsive-grid debt-datePicker white-background'}>
        <Col>
          <CustomSearch
            onChange={(e) => {
              filterData({ key: 'full_name', value: e.target.value });
            }}
            placeholder="Search for a contact"
          />
        </Col>

        <Col>
          <CustomDamageDropDown
            emptyTitle={'Payment Manager'}
            data={[{ name: '-' }, ...paymentManagers]}
            labelKey={'name'}
            valueKey={'id'}
            selectedTitle={filter?.payment_manager_id}
            handleChange={(e) => {
              localStorage.setItem('payment_manager_id', e || '');
              filterData({ key: 'payment_manager_id', value: e });
            }}
          />
        </Col>

        <Col>
          <CustomDamageDropDown
              emptyTitle={'Debt Recovery Status'}
              data={[{ label: '-' }, ...debtRecoverStatuses]}
              labelKey={"label"}
              valueKey={"value"}
              selectedTitle={filter?.debt_recovery_status}
              handleChange={(e) => {
                localStorage.setItem('debt_recovery_status', e || '');
                filterData({ key: 'debt_recovery_status', value: e });
              }}
          />
        </Col>
      </div>
      <div className={'table-container'}>
        <DebtManagementTable
          keys={keys}
          loading={loading}
          tableData={tableData}
          viewDetails={viewDetails}
          filter={orderFilter}
          setOrderItem={setOrderItem}
        />
        {!loading && pagination && (
          <Pagination
            hideDisabled
            activePage={pagination.current_page}
            totalItemsCount={pagination.total}
            itemsCountPerPage={pagination.per_page}
            onChange={(e) => {
              filterData({ key: 'page', value: e });
            }}
            itemClass="page-item"
            linkClass="page-link"
            innerClass="pagination react-bootstrap-table-page-btns-ul"
          />
        )}
      </div>
    </div>
  );
}
